/* ct-collection.js — client-side facets + sort over the baked collection
   grids (sections/ct-collection.liquid). The cards are pre-rendered from
   ct-catalog.json with data-sub / data-family / data-models / data-flags /
   data-price / data-orders / data-title; nothing here fetches anything.
   Filtering: OR within a facet group, AND across groups. Idempotent.
   8/7 design pass adds two DOM layers over the same inputs:
   — sub-category chip row ([data-ct-subs]) mirroring the sidebar's "sub"
     facets (multi-select, same semantics, same deep links);
   — active-filter chip row ([data-ct-active]) rebuilt on every filter pass,
     one chip per checked facet, hidden entirely when nothing is checked. */
(function () {
  'use strict';

  function initRoot(root) {
    if (!root || root.getAttribute('data-ct-collection-init') === '1') return;
    root.setAttribute('data-ct-collection-init', '1');

    var grid = root.querySelector('[data-ct-grid]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('.ct-card'));
    var facetInputs = Array.prototype.slice.call(root.querySelectorAll('input[data-facet]'));
    var shownEl = root.querySelector('[data-ct-shown]');
    var clearBtns = Array.prototype.slice.call(root.querySelectorAll('[data-ct-clear]'));
    var emptyEl = root.querySelector('[data-ct-empty]');
    var sortEl = root.querySelector('[data-ct-sort]');
    var restockBtn = root.querySelector('[data-ct-restock]');
    var filterToggle = root.querySelector('[data-ct-filter-toggle]');
    var facetPanel = root.querySelector('[data-ct-facet-panel]');
    var filterCount = root.querySelector('[data-ct-filter-count]');
    var activeRow = root.querySelector('[data-ct-active]');
    var activeClear = activeRow ? activeRow.querySelector('[data-ct-clear]') : null;
    var subsRow = root.querySelector('[data-ct-subs]');
    var subsTrack = root.querySelector('[data-ct-subs-track]');
    var subChips = [];

    /* ---------- filtering ---------- */

    function activeGroups() {
      var groups = {};
      facetInputs.forEach(function (input) {
        if (!input.checked) return;
        var g = input.getAttribute('data-facet');
        if (!g) return;
        (groups[g] = groups[g] || []).push(input.value);
      });
      return groups;
    }

    function listAttr(card, attr) {
      var raw = card.getAttribute(attr) || '';
      return raw ? raw.split('|') : [];
    }

    /* OR within a group, AND across groups.
       "sub" is scalar equality; "flag"/"family"/"models" are |-joined lists. */
    function cardMatches(card, groups) {
      for (var g in groups) {
        if (!Object.prototype.hasOwnProperty.call(groups, g)) continue;
        var values = groups[g];
        var ok = false;
        if (g === 'sub') {
          ok = values.indexOf(card.getAttribute('data-sub') || '') !== -1;
        } else {
          var attr = g === 'flag' ? 'data-flags' : 'data-' + g;
          var list = listAttr(card, attr);
          ok = values.some(function (v) { return list.indexOf(v) !== -1; });
        }
        if (!ok) return false;
      }
      return true;
    }

    /* The visible text of a facet input's label (first <span>). */
    function labelFor(input) {
      var label = input.closest('label');
      var span = label ? label.querySelector('span') : null;
      return span ? span.textContent.trim() : input.value;
    }

    function applyFilters() {
      var groups = activeGroups();
      var active = 0;
      for (var g in groups) {
        if (Object.prototype.hasOwnProperty.call(groups, g)) active += groups[g].length;
      }

      var shown = 0;
      cards.forEach(function (card) {
        var match = cardMatches(card, groups);
        card.classList.toggle('is-hidden', !match);
        if (match) shown++;
      });

      if (shownEl) shownEl.textContent = String(shown);
      clearBtns.forEach(function (btn) { btn.hidden = active === 0; });
      if (emptyEl) emptyEl.hidden = shown !== 0;
      grid.hidden = shown === 0 && cards.length > 0;
      if (filterCount) {
        filterCount.textContent = String(active);
        filterCount.hidden = active === 0;
      }

      renderActiveChips(active);
      syncSubChips();
    }

    /* ---------- active-filter chip row ---------- */

    function renderActiveChips(active) {
      if (!activeRow) return;
      Array.prototype.slice
        .call(activeRow.querySelectorAll('.ct-collection__active-chip'))
        .forEach(function (chip) { chip.parentNode.removeChild(chip); });
      if (active === 0) {
        activeRow.hidden = true;
        return;
      }
      facetInputs.forEach(function (input) {
        if (!input.checked) return;
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'ct-collection__active-chip';
        chip.setAttribute('data-chip-group', input.getAttribute('data-facet') || '');
        chip.setAttribute('data-chip-value', input.value);
        chip.setAttribute('aria-label', 'Remove filter: ' + labelFor(input));
        var name = document.createElement('span');
        name.textContent = labelFor(input);
        var x = document.createElement('span');
        x.className = 'ct-collection__active-x';
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        chip.appendChild(name);
        chip.appendChild(x);
        if (activeClear) activeRow.insertBefore(chip, activeClear);
        else activeRow.appendChild(chip);
      });
      activeRow.hidden = false;
    }

    /* ---------- sub-category chip row ---------- */

    function buildSubChips() {
      if (!subsRow || !subsTrack) return;
      if (subsTrack.childElementCount > 0) return; /* idempotent */
      var subInputs = facetInputs.filter(function (input) {
        return input.getAttribute('data-facet') === 'sub';
      });
      if (!subInputs.length) return; /* no sub facets → the row never shows */
      subInputs.forEach(function (input) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'ct-collection__sub-chip';
        chip.textContent = labelFor(input);
        chip.setAttribute('aria-pressed', 'false');
        if (input.disabled) {
          chip.disabled = true;
          chip.classList.add('is-zero');
        }
        subChips.push({ chip: chip, input: input });
        subsTrack.appendChild(chip);
      });
      subsRow.hidden = false;
    }

    function syncSubChips() {
      subChips.forEach(function (pair) {
        pair.chip.classList.toggle('is-on', pair.input.checked);
        pair.chip.setAttribute('aria-pressed', pair.input.checked ? 'true' : 'false');
      });
    }

    /* ---------- sort ---------- */

    function intAttr(card, attr) {
      var n = parseInt(card.getAttribute(attr), 10);
      return isNaN(n) ? 0 : n;
    }

    function compare(a, b, mode) {
      switch (mode) {
        case 'price-asc':
          return intAttr(a, 'data-price') - intAttr(b, 'data-price');
        case 'price-desc':
          return intAttr(b, 'data-price') - intAttr(a, 'data-price');
        case 'title':
          return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '');
        default: /* 'orders' */
          return intAttr(b, 'data-orders') - intAttr(a, 'data-orders');
      }
    }

    /* Stable (index tie-break), instant DOM reorder — no animation. */
    function applySort(mode) {
      var decorated = cards.map(function (card, i) { return { card: card, i: i }; });
      decorated.sort(function (a, b) {
        var d = compare(a.card, b.card, mode);
        return d !== 0 ? d : a.i - b.i;
      });
      cards = decorated.map(function (d) { return d.card; });
      cards.forEach(function (card) { grid.appendChild(card); });
    }

    /* ---------- deep links: ?family= / ?sub= / ?flag= ---------- */

    function applyDeepLinks() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }
      ['family', 'sub', 'flag'].forEach(function (g) {
        params.getAll(g).forEach(function (v) {
          facetInputs.forEach(function (input) {
            /* disabled = honest zero count — a deep link into it would only
               land on the empty grid (QA 8/7 review) */
            if (input.getAttribute('data-facet') === g && input.value === v && !input.disabled) {
              input.checked = true;
            }
          });
        });
      });
      /* ?view= is Shopify's template selector — deliberately ignored here. */
    }

    /* ---------- wiring ---------- */

    facetInputs.forEach(function (input) {
      input.addEventListener('change', applyFilters);
    });

    clearBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        facetInputs.forEach(function (input) { input.checked = false; });
        applyFilters();
      });
    });

    if (activeRow) {
      activeRow.addEventListener('click', function (e) {
        var chip = e.target.closest('.ct-collection__active-chip');
        if (!chip) return;
        var g = chip.getAttribute('data-chip-group');
        var v = chip.getAttribute('data-chip-value');
        facetInputs.forEach(function (input) {
          if (input.getAttribute('data-facet') === g && input.value === v) {
            input.checked = false;
          }
        });
        applyFilters();
      });
    }

    if (subsTrack) {
      subsTrack.addEventListener('click', function (e) {
        var chip = e.target.closest('.ct-collection__sub-chip');
        if (!chip || chip.disabled) return;
        for (var i = 0; i < subChips.length; i++) {
          if (subChips[i].chip === chip) {
            subChips[i].input.checked = !subChips[i].input.checked;
            /* a chip-driven check must not hide inside a collapsed group */
            if (subChips[i].input.checked) {
              var chipGroup = subChips[i].input.closest('details.ct-facets__group');
              if (chipGroup) chipGroup.open = true;
            }
            applyFilters();
            return;
          }
        }
      });
    }

    if (sortEl) {
      sortEl.addEventListener('change', function () {
        applySort(sortEl.value);
      });
    }

    if (restockBtn) {
      restockBtn.addEventListener('click', function () {
        if (window.CT && typeof window.CT.toast === 'function') {
          window.CT.toast('Restock alerts are coming soon — for now call 864-973-7973 and we’ll watch it for you.');
        }
      });
    }

    if (filterToggle && facetPanel) {
      filterToggle.addEventListener('click', function () {
        var open = facetPanel.classList.toggle('is-open');
        filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    buildSubChips();
    applyDeepLinks();
    /* Collapsible facet groups (8/18): a deep-linked check hidden inside a
       closed <details> group would read as a ghost filter — open its group.
       Null-safe when a snippet renders flat (non-details) groups. */
    facetInputs.forEach(function (input) {
      if (!input.checked) return;
      var group = input.closest('details.ct-facets__group');
      if (group) group.open = true;
    });
    applyFilters();
    /* A deep-linked sub selection can sit off-screen in the chip rail at
       390px — bring it into view without scrolling the page (QA 8/7 review) */
    var onChip = root.querySelector('.ct-collection__sub-chip.is-on');
    if (onChip && typeof onChip.scrollIntoView === 'function') {
      onChip.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    }
    /* No initial sort: the baked grids arrive pre-ordered (orders desc;
       closeout by savings), which is the "Most ordered" default. */
  }

  function init() {
    document.querySelectorAll('[data-ct-collection]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
